import { useCallback, useEffect, useMemo, useState } from "react";
import { listManufacturingPlants, listManufacturingUnits } from "@/lib/manufacturingClient";
import type { MfgPlant, MfgUnit } from "@/lib/manufacturing/types";
import { listOpsDeals } from "@/lib/opsPaperworkClient";
import { DEALS_PICKER_PAGE_SIZE } from "@/lib/ops/opsDealsListQuery";
import type { DealRow } from "@/components/ops/opsPaperworkTypes";
import { listServiceEvents, listServiceRequests } from "@/lib/service/client";
import {
  clearOpsLookupFocus,
  setOpsLookupFocus,
} from "@/lib/opsLookup/opsLookupFocusSurface";
import {
  filterOrderRows,
  filterSalesRows,
  filterServiceRows,
  indexUnitsByDealId,
  indexUnitsById,
  mergeServiceLookupRows,
  servicesForUnitId,
  type OpsLookupSaleRow,
  type OpsLookupServiceRow,
  type OpsLookupSlider,
  type OpsLookupUnitRow,
} from "@/lib/opsLookup/opsLookupModel";

function toUnitRow(u: MfgUnit): OpsLookupUnitRow {
  return {
    id: u.id,
    serial: u.serial,
    display_label: u.display_label,
    sale_path: u.sale_path,
    plant_id: u.plant_id,
    ops_deal_id: u.ops_deal_id,
    inventory_holder_org_id: u.inventory_holder_org_id,
    end_customer_contact_id: u.end_customer_contact_id,
    current_phase_id: u.current_phase_id,
    staged_at: u.staged_at,
    delivered_at: u.delivered_at,
    notes: u.notes ?? "",
    customer_name: u.customer_name ?? null,
    seller_location_name: u.seller_location_name ?? null,
    deal_display_name: u.deal_display_name ?? null,
  };
}

function toSaleRow(d: DealRow): OpsLookupSaleRow {
  return {
    id: d.id,
    display_name: d.display_name,
    status: d.status,
    completeness: d.completeness,
    paperwork_missing: d.paperwork_missing,
  };
}

function pickVisibleId(ids: string[], current: string | null): string | null {
  if (current && ids.includes(current)) return current;
  return ids[0] ?? null;
}

export function useOpsLookup(organizationId: string | null) {
  const [slider, setSlider] = useState<OpsLookupSlider>("orders");
  const [query, setQuery] = useState("");
  const [sales, setSales] = useState<OpsLookupSaleRow[]>([]);
  const [units, setUnits] = useState<OpsLookupUnitRow[]>([]);
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [services, setServices] = useState<OpsLookupServiceRow[]>([]);
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [softWarning, setSoftWarning] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!organizationId) {
      setSales([]);
      setUnits([]);
      setPlants([]);
      setServices([]);
      return;
    }
    setLoading(true);
    setError(null);
    setSoftWarning(null);
    const [dealsRes, unitsRes, plantsRes, eventsRes, requestsRes] = await Promise.all([
      listOpsDeals({ status: "active", page_size: DEALS_PICKER_PAGE_SIZE }),
      listManufacturingUnits(),
      listManufacturingPlants(),
      listServiceEvents(),
      listServiceRequests(),
    ]);
    setLoading(false);

    const errors: string[] = [];
    const soft: string[] = [];
    if (!dealsRes.ok) errors.push(dealsRes.error || "Failed to load sales");
    else {
      const payload = dealsRes.data as { deals?: DealRow[] };
      setSales((payload.deals ?? []).map(toSaleRow));
    }
    if (!unitsRes.ok) {
      soft.push(unitsRes.error || "Orders unavailable (manufacturing entitlement may be required)");
      setUnits([]);
    } else {
      const list = Array.isArray(unitsRes.data?.units) ? unitsRes.data.units : [];
      setUnits(list.map(toUnitRow));
    }
    if (!plantsRes.ok) {
      setPlants([]);
    } else {
      setPlants(Array.isArray(plantsRes.data?.plants) ? plantsRes.data.plants : []);
    }

    if (!eventsRes.ok && !requestsRes.ok) {
      soft.push("Service unavailable");
      setServices([]);
    } else {
      setServices(
        mergeServiceLookupRows({
          events: eventsRes.ok ? eventsRes.data.events ?? [] : [],
          requests: requestsRes.ok ? requestsRes.data.requests ?? [] : [],
        }),
      );
    }

    if (errors.length) setError(errors.join(" · "));
    if (soft.length) setSoftWarning(soft.join(" · "));
  }, [organizationId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const unitsByDealId = useMemo(() => indexUnitsByDealId(units), [units]);
  const unitsById = useMemo(() => indexUnitsById(units), [units]);
  const plantNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of plants) m.set(p.id, p.name || p.plant_code || p.id);
    return m;
  }, [plants]);
  const plantById = useMemo(() => {
    const m = new Map<string, MfgPlant>();
    for (const p of plants) m.set(p.id, p);
    return m;
  }, [plants]);

  const filteredSales = useMemo(
    () => filterSalesRows(sales, unitsByDealId, query),
    [sales, unitsByDealId, query],
  );
  const filteredOrders = useMemo(() => filterOrderRows(units, query), [units, query]);
  const filteredServices = useMemo(
    () => filterServiceRows(services, unitsById, query),
    [services, unitsById, query],
  );

  // Keep selection on a visible row so detail stays populated during filter/reload.
  useEffect(() => {
    const next = pickVisibleId(
      filteredSales.map((r) => r.id),
      selectedSaleId,
    );
    if (next !== selectedSaleId) setSelectedSaleId(next);
  }, [filteredSales, selectedSaleId]);

  useEffect(() => {
    const next = pickVisibleId(
      filteredOrders.map((r) => r.id),
      selectedOrderId,
    );
    if (next !== selectedOrderId) setSelectedOrderId(next);
  }, [filteredOrders, selectedOrderId]);

  useEffect(() => {
    const next = pickVisibleId(
      filteredServices.map((r) => r.id),
      selectedServiceId,
    );
    if (next !== selectedServiceId) setSelectedServiceId(next);
  }, [filteredServices, selectedServiceId]);

  useEffect(() => {
    if (!organizationId) {
      clearOpsLookupFocus();
      return;
    }
    setOpsLookupFocus({
      slider,
      selected_sale_id: selectedSaleId,
      selected_order_id: selectedOrderId,
      selected_service_id: selectedServiceId,
    });
    return () => clearOpsLookupFocus();
  }, [organizationId, slider, selectedSaleId, selectedOrderId, selectedServiceId]);

  const selectedSale = useMemo(
    () => sales.find((s) => s.id === selectedSaleId) ?? null,
    [sales, selectedSaleId],
  );
  const selectedOrder = useMemo(
    () => units.find((u) => u.id === selectedOrderId) ?? null,
    [units, selectedOrderId],
  );
  const selectedService = useMemo(
    () => services.find((s) => s.id === selectedServiceId) ?? null,
    [services, selectedServiceId],
  );

  const jumpToSale = useCallback((dealId: string | null) => {
    if (!dealId) return;
    setSelectedSaleId(dealId);
    setSlider("sales");
  }, []);

  const jumpToOrder = useCallback((unitId: string | null) => {
    if (!unitId) return;
    setSelectedOrderId(unitId);
    setSlider("orders");
  }, []);

  const jumpToService = useCallback((serviceId: string | null) => {
    if (!serviceId) return;
    setSelectedServiceId(serviceId);
    setSlider("service");
  }, []);

  const relatedForSelectedSale = useMemo(() => {
    const unit = selectedSale ? unitsByDealId.get(selectedSale.id) : null;
    return servicesForUnitId(services, unit?.id);
  }, [selectedSale, unitsByDealId, services]);

  const relatedForSelectedOrder = useMemo(
    () => servicesForUnitId(services, selectedOrder?.id),
    [services, selectedOrder],
  );

  return {
    slider,
    setSlider,
    query,
    setQuery,
    loading,
    error,
    softWarning,
    reload,
    sales,
    services,
    filteredSales,
    filteredOrders,
    filteredServices,
    selectedSale,
    selectedOrder,
    selectedService,
    selectedSaleId,
    selectedOrderId,
    selectedServiceId,
    setSelectedSaleId,
    setSelectedOrderId,
    setSelectedServiceId,
    unitsByDealId,
    unitsById,
    plantNameById,
    plantById,
    jumpToSale,
    jumpToOrder,
    jumpToService,
    relatedForSelectedSale,
    relatedForSelectedOrder,
  };
}
